import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Pencil, Eraser, Square, Circle, Minus, Undo2, Redo2,
  Layers, Eye, EyeOff, Trash2, Plus, ChevronDown, ChevronRight,
  Download, PaintBucket, MousePointer, Move, RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ───────── types ───────── */
type Tool = "brush" | "eraser" | "rect" | "circle" | "line" | "fill" | "select";

interface DrawingLayer {
  id: string;
  name: string;
  visible: boolean;
  opacity: number;
  canvas: HTMLCanvasElement | null; // off-screen canvas per layer
}

interface HistoryEntry {
  layerId: string;
  imageData: ImageData;
}

interface DrawingCanvasProps {
  width?: number;
  height?: number;
  /** Seed image to load as bottom layer */
  initialImage?: string | null;
  /** Called when user exports / saves the merged canvas */
  onExport?: (dataUrl: string) => void;
  /** Called after every stroke ends — use for auto-saving */
  onChange?: (dataUrl: string) => void;
  className?: string;
}

const COLORS = [
  "#000000", "#ffffff", "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#3b82f6", "#8b5cf6", "#ec4899", "#6b7280", "#92400e", "#065f46",
  "#1e3a5f", "#7c3aed", "#be185d", "#0ea5e9",
];

const MAX_HISTORY = 40;

export function DrawingCanvas({
  width = 768, height = 768, initialImage, onExport, onChange, className,
}: DrawingCanvasProps) {
  /* ── layer state ── */
  const [layers, setLayers] = useState<DrawingLayer[]>([]);
  const [activeLayerId, setActiveLayerId] = useState<string>("");
  const [showLayers, setShowLayers] = useState(true);

  /* ── tool state ── */
  const [tool, setTool] = useState<Tool>("brush");
  const [brushSize, setBrushSize] = useState(4);
  const [color, setColor] = useState("#000000");
  const [customColor, setCustomColor] = useState("#000000");

  /* ── undo / redo ── */
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  /* ── drawing state ── */
  const compositeRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const shapeStart = useRef<{ x: number; y: number } | null>(null);
  const shapePreviewRef = useRef<HTMLCanvasElement>(null); // overlay for shape preview
  const layerCanvasMap = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /* ── helpers ── */
  const getLayerCanvas = useCallback((id: string): HTMLCanvasElement | null => {
    return layerCanvasMap.current.get(id) ?? null;
  }, []);

  const createOffscreenCanvas = useCallback(() => {
    const c = document.createElement("canvas");
    c.width = width;
    c.height = height;
    return c;
  }, [width, height]);

  /* ── initialise layers ── */
  useEffect(() => {
    const bgId = crypto.randomUUID();
    const drawId = crypto.randomUUID();

    const bgCanvas = document.createElement("canvas");
    bgCanvas.width = width;
    bgCanvas.height = height;
    const bgCtx = bgCanvas.getContext("2d")!;
    bgCtx.fillStyle = "#ffffff";
    bgCtx.fillRect(0, 0, width, height);

    const drawCanvas = document.createElement("canvas");
    drawCanvas.width = width;
    drawCanvas.height = height;

    layerCanvasMap.current.set(bgId, bgCanvas);
    layerCanvasMap.current.set(drawId, drawCanvas);

    setLayers([
      { id: bgId, name: "Background", visible: true, opacity: 1, canvas: bgCanvas },
      { id: drawId, name: "Draw Layer", visible: true, opacity: 1, canvas: drawCanvas },
    ]);
    setActiveLayerId(drawId);

    // load initial image into background
    if (initialImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        bgCtx.clearRect(0, 0, width, height);
        // fit image maintaining aspect ratio
        const scale = Math.min(width / img.width, height / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        bgCtx.fillStyle = "#ffffff";
        bgCtx.fillRect(0, 0, width, height);
        bgCtx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
        compositeAllLayers();
      };
      img.src = initialImage;
    }
  }, [initialImage, width, height]);

  /* ── composite all layers to visible canvas ── */
  const compositeAllLayers = useCallback(() => {
    const comp = compositeRef.current;
    if (!comp) return;
    const ctx = comp.getContext("2d")!;
    ctx.clearRect(0, 0, width, height);

    // draw checkerboard for transparency
    ctx.fillStyle = "#e5e7eb";
    ctx.fillRect(0, 0, width, height);
    const sq = 16;
    ctx.fillStyle = "#d1d5db";
    for (let y = 0; y < height; y += sq) {
      for (let x = 0; x < width; x += sq) {
        if ((x / sq + y / sq) % 2 === 0) ctx.fillRect(x, y, sq, sq);
      }
    }

    for (const layer of layers) {
      if (!layer.visible) continue;
      const lc = layerCanvasMap.current.get(layer.id);
      if (!lc) continue;
      ctx.globalAlpha = layer.opacity;
      ctx.drawImage(lc, 0, 0);
    }
    ctx.globalAlpha = 1;
  }, [layers, width, height]);

  useEffect(() => {
    compositeAllLayers();
  }, [layers, compositeAllLayers]);

  /* ── save state for undo ── */
  const pushHistory = useCallback(() => {
    const lc = getLayerCanvas(activeLayerId);
    if (!lc) return;
    const ctx = lc.getContext("2d")!;
    const imageData = ctx.getImageData(0, 0, width, height);
    const entry: HistoryEntry = { layerId: activeLayerId, imageData };
    setHistory(prev => {
      const newHist = prev.slice(0, historyIndex + 1);
      newHist.push(entry);
      if (newHist.length > MAX_HISTORY) newHist.shift();
      return newHist;
    });
    setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [activeLayerId, getLayerCanvas, historyIndex, width, height]);

  const undo = useCallback(() => {
    if (historyIndex < 0) return;
    const entry = history[historyIndex];
    const lc = layerCanvasMap.current.get(entry.layerId);
    if (lc) {
      const ctx = lc.getContext("2d")!;
      ctx.putImageData(entry.imageData, 0, 0);
      compositeAllLayers();
    }
    setHistoryIndex(prev => prev - 1);
  }, [history, historyIndex, compositeAllLayers]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const entry = history[historyIndex + 1];
    const lc = layerCanvasMap.current.get(entry.layerId);
    if (lc) {
      const ctx = lc.getContext("2d")!;
      ctx.putImageData(entry.imageData, 0, 0);
      compositeAllLayers();
    }
    setHistoryIndex(prev => prev + 1);
  }, [history, historyIndex, compositeAllLayers]);

  /* ── keyboard shortcuts ── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  /* ── get canvas coords from mouse/touch ── */
  const getCanvasPoint = useCallback((e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
    const comp = compositeRef.current;
    if (!comp) return null;
    const rect = comp.getBoundingClientRect();
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    let clientX: number, clientY: number;
    if ("touches" in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  }, [width, height]);

  /* ── drawing functions ── */
  const drawLine = useCallback((ctx: CanvasRenderingContext2D, from: { x: number; y: number }, to: { x: number; y: number }) => {
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.strokeStyle = tool === "eraser" ? "rgba(0,0,0,1)" : color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (tool === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
    } else {
      ctx.globalCompositeOperation = "source-over";
    }
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
  }, [tool, color, brushSize]);

  const floodFill = useCallback((x: number, y: number) => {
    const lc = getLayerCanvas(activeLayerId);
    if (!lc) return;
    const ctx = lc.getContext("2d")!;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const targetIdx = (Math.floor(y) * width + Math.floor(x)) * 4;
    const tr = data[targetIdx], tg = data[targetIdx + 1], tb = data[targetIdx + 2], ta = data[targetIdx + 3];

    // parse fill color
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = 1; tempCanvas.height = 1;
    const tempCtx = tempCanvas.getContext("2d")!;
    tempCtx.fillStyle = color;
    tempCtx.fillRect(0, 0, 1, 1);
    const fillData = tempCtx.getImageData(0, 0, 1, 1).data;
    const fr = fillData[0], fg = fillData[1], fb = fillData[2], fa = fillData[3];

    if (tr === fr && tg === fg && tb === fb && ta === fa) return;

    const tolerance = 30;
    const match = (i: number) =>
      Math.abs(data[i] - tr) <= tolerance &&
      Math.abs(data[i + 1] - tg) <= tolerance &&
      Math.abs(data[i + 2] - tb) <= tolerance &&
      Math.abs(data[i + 3] - ta) <= tolerance;

    const stack = [Math.floor(x), Math.floor(y)];
    const visited = new Uint8Array(width * height);
    while (stack.length > 0) {
      const py = stack.pop()!;
      const px = stack.pop()!;
      if (px < 0 || px >= width || py < 0 || py >= height) continue;
      const idx = py * width + px;
      if (visited[idx]) continue;
      visited[idx] = 1;
      const pi = idx * 4;
      if (!match(pi)) continue;
      data[pi] = fr; data[pi + 1] = fg; data[pi + 2] = fb; data[pi + 3] = fa;
      stack.push(px + 1, py, px - 1, py, px, py + 1, px, py - 1);
    }
    ctx.putImageData(imageData, 0, 0);
  }, [activeLayerId, color, getLayerCanvas, width, height]);

  /* ── mouse handlers ── */
  const handlePointerDown = useCallback((e: React.MouseEvent) => {
    const pt = getCanvasPoint(e);
    if (!pt) return;
    isDrawing.current = true;
    lastPoint.current = pt;

    if (tool === "fill") {
      pushHistory();
      floodFill(pt.x, pt.y);
      compositeAllLayers();
      isDrawing.current = false;
      return;
    }
    if (tool === "rect" || tool === "circle" || tool === "line") {
      shapeStart.current = pt;
      pushHistory();
      return;
    }
    if (tool === "brush" || tool === "eraser") {
      pushHistory();
      const lc = getLayerCanvas(activeLayerId);
      if (!lc) return;
      const ctx = lc.getContext("2d")!;
      // Draw a dot at the start point
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, brushSize / 2, 0, Math.PI * 2);
      if (tool === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "rgba(0,0,0,1)";
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = color;
      }
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      compositeAllLayers();
    }
  }, [getCanvasPoint, tool, pushHistory, floodFill, compositeAllLayers, activeLayerId, getLayerCanvas, brushSize, color]);

  const handlePointerMove = useCallback((e: React.MouseEvent) => {
    if (!isDrawing.current) return;
    const pt = getCanvasPoint(e);
    if (!pt) return;

    if ((tool === "brush" || tool === "eraser") && lastPoint.current) {
      const lc = getLayerCanvas(activeLayerId);
      if (!lc) return;
      const ctx = lc.getContext("2d")!;
      drawLine(ctx, lastPoint.current, pt);
      lastPoint.current = pt;
      compositeAllLayers();
      return;
    }

    // Shape preview
    if ((tool === "rect" || tool === "circle" || tool === "line") && shapeStart.current) {
      const preview = shapePreviewRef.current;
      if (!preview) return;
      const ctx = preview.getContext("2d")!;
      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.setLineDash([6, 4]);
      const s = shapeStart.current;
      if (tool === "rect") {
        ctx.strokeRect(s.x, s.y, pt.x - s.x, pt.y - s.y);
      } else if (tool === "circle") {
        const rx = Math.abs(pt.x - s.x) / 2;
        const ry = Math.abs(pt.y - s.y) / 2;
        const cx = s.x + (pt.x - s.x) / 2;
        const cy = s.y + (pt.y - s.y) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (tool === "line") {
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }
  }, [tool, getCanvasPoint, activeLayerId, getLayerCanvas, drawLine, compositeAllLayers, color, brushSize, width, height]);

  const handlePointerUp = useCallback((e: React.MouseEvent) => {
    if (!isDrawing.current) return;
    isDrawing.current = false;

    // Finalize shape
    if ((tool === "rect" || tool === "circle" || tool === "line") && shapeStart.current) {
      const pt = getCanvasPoint(e);
      if (!pt) return;
      const lc = getLayerCanvas(activeLayerId);
      if (!lc) return;
      const ctx = lc.getContext("2d")!;
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = "round";
      const s = shapeStart.current;

      if (tool === "rect") {
        ctx.strokeRect(s.x, s.y, pt.x - s.x, pt.y - s.y);
      } else if (tool === "circle") {
        const rx = Math.abs(pt.x - s.x) / 2;
        const ry = Math.abs(pt.y - s.y) / 2;
        const cx = s.x + (pt.x - s.x) / 2;
        const cy = s.y + (pt.y - s.y) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (tool === "line") {
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
      }

      // clear preview
      const preview = shapePreviewRef.current;
      if (preview) {
        preview.getContext("2d")!.clearRect(0, 0, width, height);
      }
      shapeStart.current = null;
      compositeAllLayers();
    }

    lastPoint.current = null;

    // Auto-save after every stroke
    if (onChangeRef.current) {
      requestAnimationFrame(() => {
        const merged = document.createElement("canvas");
        merged.width = width;
        merged.height = height;
        const mctx = merged.getContext("2d")!;
        for (const layer of layers) {
          if (!layer.visible) continue;
          const lc2 = layerCanvasMap.current.get(layer.id);
          if (!lc2) continue;
          mctx.globalAlpha = layer.opacity;
          mctx.drawImage(lc2, 0, 0);
        }
        mctx.globalAlpha = 1;
        onChangeRef.current?.(merged.toDataURL("image/png"));
      });
    }
  }, [tool, getCanvasPoint, activeLayerId, getLayerCanvas, color, brushSize, compositeAllLayers, width, height, layers]);

  /* ── layer operations ── */
  const addLayer = useCallback(() => {
    const id = crypto.randomUUID();
    const c = createOffscreenCanvas();
    layerCanvasMap.current.set(id, c);
    setLayers(prev => [...prev, { id, name: `Layer ${prev.length + 1}`, visible: true, opacity: 1, canvas: c }]);
    setActiveLayerId(id);
  }, [createOffscreenCanvas]);

  const removeLayer = useCallback((id: string) => {
    if (layers.length <= 1) return;
    layerCanvasMap.current.delete(id);
    setLayers(prev => prev.filter(l => l.id !== id));
    if (activeLayerId === id) {
      setActiveLayerId(layers.find(l => l.id !== id)?.id || "");
    }
  }, [layers, activeLayerId]);

  const toggleLayerVisibility = useCallback((id: string) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, visible: !l.visible } : l));
  }, []);

  const setLayerOpacity = useCallback((id: string, opacity: number) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, opacity } : l));
  }, []);

  const renameLayer = useCallback((id: string, name: string) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, name } : l));
  }, []);

  const clearActiveLayer = useCallback(() => {
    const lc = getLayerCanvas(activeLayerId);
    if (!lc) return;
    pushHistory();
    const ctx = lc.getContext("2d")!;
    ctx.clearRect(0, 0, width, height);
    compositeAllLayers();
  }, [activeLayerId, getLayerCanvas, pushHistory, compositeAllLayers, width, height]);

  /* ── get merged data url (no side effects) ── */
  const getMergedDataUrl = useCallback(() => {
    const merged = document.createElement("canvas");
    merged.width = width;
    merged.height = height;
    const ctx = merged.getContext("2d")!;
    for (const layer of layers) {
      if (!layer.visible) continue;
      const lc = layerCanvasMap.current.get(layer.id);
      if (!lc) continue;
      ctx.globalAlpha = layer.opacity;
      ctx.drawImage(lc, 0, 0);
    }
    ctx.globalAlpha = 1;
    return merged.toDataURL("image/png");
  }, [layers, width, height]);

  /* ── export ── */
  const exportCanvas = useCallback(() => {
    const dataUrl = getMergedDataUrl();
    onExport?.(dataUrl);
    return dataUrl;
  }, [getMergedDataUrl, onExport]);

  /* ── tool config ── */
  const tools: { id: Tool; icon: React.ReactNode; label: string }[] = [
    { id: "brush", icon: <Pencil className="w-4 h-4" />, label: "Brush" },
    { id: "eraser", icon: <Eraser className="w-4 h-4" />, label: "Eraser" },
    { id: "fill", icon: <PaintBucket className="w-4 h-4" />, label: "Fill" },
    { id: "line", icon: <Minus className="w-4 h-4" />, label: "Line" },
    { id: "rect", icon: <Square className="w-4 h-4" />, label: "Rectangle" },
    { id: "circle", icon: <Circle className="w-4 h-4" />, label: "Ellipse" },
  ];

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* ── Toolbar ── */}
      <div className="flex items-center gap-1 flex-wrap bg-muted/30 rounded-lg p-1.5 border border-border">
        {tools.map(t => (
          <Button
            key={t.id}
            variant={tool === t.id ? "default" : "ghost"}
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setTool(t.id)}
            title={t.label}
          >
            {t.icon}
          </Button>
        ))}

        <div className="w-px h-5 bg-border mx-1" />

        {/* Brush size */}
        <div className="flex items-center gap-1.5 px-1">
          <span className="text-[10px] text-muted-foreground">Size</span>
          <Slider
            value={[brushSize]}
            onValueChange={v => setBrushSize(v[0])}
            min={1} max={40} step={1}
            className="w-20"
          />
          <span className="text-[10px] text-muted-foreground w-5 text-right">{brushSize}</span>
        </div>

        <div className="w-px h-5 bg-border mx-1" />

        {/* Color palette */}
        <div className="flex items-center gap-0.5 flex-wrap">
          {COLORS.map(c => (
            <button
              key={c}
              className={cn(
                "w-5 h-5 rounded-sm border transition-all",
                color === c ? "ring-2 ring-primary ring-offset-1 ring-offset-background border-primary" : "border-border hover:scale-110"
              )}
              style={{ backgroundColor: c }}
              onClick={() => { setColor(c); setCustomColor(c); }}
            />
          ))}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="w-5 h-5 rounded-sm border border-border bg-gradient-to-br from-red-500 via-green-500 to-blue-500 hover:scale-110 transition-transform"
                title="Custom color"
              />
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="start">
              <Input
                type="color"
                value={customColor}
                onChange={e => { setCustomColor(e.target.value); setColor(e.target.value); }}
                className="h-8 w-20 p-0 border-none cursor-pointer"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="w-px h-5 bg-border mx-1" />

        {/* Undo / Redo */}
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={undo} disabled={historyIndex < 0} title="Undo (Ctrl+Z)">
          <Undo2 className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={redo} disabled={historyIndex >= history.length - 1} title="Redo (Ctrl+Y)">
          <Redo2 className="w-3.5 h-3.5" />
        </Button>

        <div className="w-px h-5 bg-border mx-1" />

        <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] gap-1" onClick={clearActiveLayer} title="Clear active layer">
          <RotateCcw className="w-3 h-3" /> Clear
        </Button>

        <div className="flex-1" />

        <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] gap-1" onClick={() => setShowLayers(!showLayers)}>
          <Layers className="w-3.5 h-3.5" /> Layers
          {showLayers ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </Button>

        <Button variant="outline" size="sm" className="h-7 px-2 text-[11px] gap-1" onClick={exportCanvas}>
          <Download className="w-3 h-3" /> Export
        </Button>
      </div>

      <div className="flex gap-2">
        {/* ── Canvas ── */}
        <div className="flex-1 relative bg-muted/20 rounded-lg border border-border overflow-hidden flex items-center justify-center p-2">
          <div className="relative" style={{ maxWidth: "100%", maxHeight: "100%" }}>
            <canvas
              ref={compositeRef}
              width={width}
              height={height}
              className="max-w-full max-h-[60vh] rounded cursor-crosshair"
              style={{ imageRendering: "auto" }}
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onMouseLeave={() => { isDrawing.current = false; lastPoint.current = null; }}
            />
            {/* Shape preview overlay */}
            <canvas
              ref={shapePreviewRef}
              width={width}
              height={height}
              className="absolute inset-0 max-w-full max-h-[60vh] rounded pointer-events-none"
              style={{ imageRendering: "auto" }}
            />
          </div>
        </div>

        {/* ── Layers Panel ── */}
        {showLayers && (
          <div className="w-48 shrink-0 border border-border rounded-lg bg-background p-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground flex items-center gap-1">
                <Layers className="w-3.5 h-3.5" /> Layers
              </span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={addLayer} title="Add layer">
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            <ScrollArea className="max-h-48">
              <div className="space-y-0.5">
                {[...layers].reverse().map(layer => (
                  <div
                    key={layer.id}
                    className={cn(
                      "flex items-center gap-1 px-1.5 py-1 rounded text-xs cursor-pointer transition-colors",
                      activeLayerId === layer.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50 text-foreground"
                    )}
                    onClick={() => setActiveLayerId(layer.id)}
                  >
                    <button className="shrink-0" onClick={e => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}>
                      {layer.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3 text-muted-foreground" />}
                    </button>
                    <span className="flex-1 truncate text-[11px]">{layer.name}</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="opacity-0 group-hover:opacity-100 hover:opacity-100 shrink-0" onClick={e => e.stopPropagation()}>
                          <ChevronDown className="w-3 h-3 text-muted-foreground" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-40 p-2 space-y-2" align="end">
                        <Input
                          value={layer.name}
                          onChange={e => renameLayer(layer.id, e.target.value)}
                          className="h-7 text-xs"
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground">Opacity</span>
                          <Slider
                            value={[layer.opacity * 100]}
                            onValueChange={v => setLayerOpacity(layer.id, v[0] / 100)}
                            min={0} max={100} step={5}
                            className="flex-1"
                          />
                        </div>
                        {layers.length > 1 && (
                          <Button variant="destructive" size="sm" className="w-full h-6 text-[10px] gap-1" onClick={() => removeLayer(layer.id)}>
                            <Trash2 className="w-3 h-3" /> Delete
                          </Button>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}
